import type { Role, User } from '@lucidtech/las-sdk-browser';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useCallback, useMemo, useState } from 'react';
import { Key, ListBoxSection } from 'react-aria-components';

import {
  AuxActionButton,
  Button,
  Flex,
  ListBoxItem,
  PillSelectValue,
  Save,
  SelectPills,
  SelectPillsOption,
  Table,
  Text,
} from '@/components/core';
import { ManagedRole } from '@/constants';
import { toUserId, useDeleteUser, useListRoles, useListUsers, useUpdateUser } from '@/hooks/api';
import { Id } from '@/utils';

import { AddIcon, SaveIcon } from '../icons';
import { ListRolesModal } from './ListRolesModal';
import { Profile } from './Profile';
import { RoleBadge, RoleIcon } from './RolesTable';

const ColumnHeader = ({ header }: { header: string }) => {
  return (
    <div className="px-4 py-1">
      <Text className="!text-xs font-medium text-gray-500">{header}</Text>
    </div>
  );
};

export type MembersTableProps = object;

// eslint-disable-next-line no-empty-pattern
const IMembersTable = ({}: MembersTableProps) => {
  const { data: users, isLoading } = useListUsers();
  const { mutate: deleteUser } = useDeleteUser();
  const { data: roles } = useListRoles();
  const { mutateAsync: updateUser } = useUpdateUser();

  const [showRolesModal, setShowRolesModal] = useState(false);
  const [userIsUpdating, setUserIsUpdating] = useState<string | null>(null);

  const auxItems = useMemo(
    () => (
      <ListBoxSection aria-label="Actions">
        <ListBoxItem isDisabled>
          <AuxActionButton className="!p-0" variant="plain" onPress={() => setShowRolesModal(true)}>
            <Text className="!text-primary-500">Edit roles</Text>
          </AuxActionButton>
        </ListBoxItem>
      </ListBoxSection>
    ),
    []
  );

  const onUpdateUser = useCallback(
    (userId: string, roleKeys: Key[]) => {
      const roleIds = roleKeys?.map((r) => r.toString()) ?? [];
      const user = users?.data.find((user) => user.userId === userId);

      const isAdmin = user?.roleIds.includes(ManagedRole.Admin);
      const numAdmins = users?.data.filter((user) => user.roleIds.includes(ManagedRole.Admin)).length ?? 0;
      const isOnlyAdmin = isAdmin && numAdmins === 1;

      if (!isOnlyAdmin) {
        setUserIsUpdating(userId);

        updateUser({
          id: toUserId(userId),
          updates: { roleIds },
        }).finally(() => {
          setUserIsUpdating(null);
        });
      }
    },
    [updateUser, users?.data]
  );

  const columns = useMemo<ColumnDef<User & Id>[]>(() => {
    return [
      {
        header: () => <ColumnHeader header="Member" />,
        accessorKey: 'userId',
        cell: ({ row }) => (
          <Profile
            className="mx-4 my-2"
            profileId={row.original.profileId}
            props={{ img: { className: 'size-8' } }}
            showName
            showEmail
            disableTooltip
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
      {
        header: () => <ColumnHeader header="Roles" />,
        accessorKey: 'roleIds',
        cell: ({ row }) => {
          const items = roles?.data?.map((r): Role & Id => ({ ...r, id: r.roleId }));
          const defaultSelected = items?.filter((r) => row.original.roleIds.includes(r.roleId)).map((r) => r.roleId);

          const buttonComponent = (
            <Button className="group !border-0 bg-transparent hover:bg-transparent">
              <PillSelectValue>
                {(item?: (Role & Id) | null) => {
                  return item?.roleId ? <RoleBadge key={item.id} roleId={item?.roleId} /> : null;
                }}
              </PillSelectValue>
              {userIsUpdating === row.original.userId ? (
                <SaveIcon />
              ) : (
                <div className="invisible rounded-sm p-1 group-hover/row:visible hover:bg-gray-100">
                  <AddIcon />
                </div>
              )}
            </Button>
          );

          return (
            <div className="flex flex-row gap-1">
              <SelectPills<Role & Id>
                key={row.original.id}
                onChange={(roleIds) => onUpdateUser(row.original.userId, roleIds)}
                optionRenderFn={(option) => (
                  <SelectPillsOption value={option}>
                    <Flex className="gap-sm items-center border-none">
                      <RoleIcon roleId={option?.roleId} className="size-3" />
                      <Text>{option?.name}</Text>
                    </Flex>
                  </SelectPillsOption>
                )}
                defaultValue={defaultSelected}
                items={items}
                buttonComponent={buttonComponent}
                auxItems={auxItems}
              />
            </div>
          );
        },
        size: 0,
      },
    ];
  }, [auxItems, onUpdateUser, roles?.data, userIsUpdating]);

  const data = useMemo(() => users?.data.map((u) => ({ ...u, id: u.userId })) ?? [], [users]);

  return isLoading ? (
    <Save message="Loading members ... " />
  ) : (
    <Flex col variant="secondary" className="rounded-lg shadow-xs">
      <Table
        props={{
          row: { className: 'group/row border-b border-gray-200 hover:bg-gray-25' },
          header: { className: 'rounded-lg bg-gray-50 border-b border-gray-200' },
        }}
        columns={columns}
        data={data}
        hideDragHandle
        deleteRowAction={(row) => deleteUser({ userId: row.original.userId })}
      />
      <ListRolesModal isOpen={showRolesModal} onOpenChange={(val: boolean) => setShowRolesModal(val)} />
    </Flex>
  );
};

export const MembersTable = memo(IMembersTable) as typeof IMembersTable;
